/**
 * Month view for shift planning
 * Displays a grid with rows = employees, columns = days of the month
 * Shows compact colored badges per shift and absence overlays
 */

import { useMemo, useCallback } from 'react';
import { useLingui } from '@lingui/react';
import {
  format,
  getDaysInMonth,
  startOfMonth,
  addDays,
  isWeekend,
  isToday,
  parseISO,
} from 'date-fns';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Shift } from '@/types/shifts';
import type { AbsenceTypeValue, EmployeeAbsence } from '@/types/employeeAbsences';
import { statusToneClasses } from '@/components/ui/status-badge';
import { ABSENCE_TYPE_TONES, getAbsenceTypeLabel } from '@/components/absences/AbsenceTypeBadge';
import { formatDecimal, formatWeekday } from '@/lib/format';
import { ShiftStatusBadge } from './ShiftStatusBadge';
import { shiftBlockClasses } from './shiftLifecycle';
import { OPEN_SHIFT_BLOCK_CLASSES, OPEN_SHIFT_VALUE } from './openShifts';
import { ShiftTypeBadge } from './ShiftTypeBadge';

interface EmployeeData {
  id?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
}

interface ShiftMonthViewProps {
  shifts: Shift[];
  absences: EmployeeAbsence[];
  currentDate: Date;
  employeeMap: Map<string, EmployeeData>;
  onShiftClick: (shift: Shift) => void;
  onCellClick: (date: Date, employeeId: string) => void;
  filterChildcareId?: string | null;
  filterGroupId?: string | null;
}

/** Absence marker colours: the absence type badge's tone, as in the week view. */
function getAbsenceColor(absenceType?: string): string {
  const tone = ABSENCE_TYPE_TONES[absenceType as AbsenceTypeValue]?.tone ?? 'neutral';
  return statusToneClasses[tone];
}

/** Calculate net hours for a shift */
function calcNetHours(shift: Shift): number {
  if (shift.net_hours !== undefined && shift.net_hours !== null) {
    return shift.net_hours;
  }
  // Fallback calculation
  const [startH, startM] = (shift.start_time ?? '00:00').split(':').map(Number);
  const [endH, endM] = (shift.end_time ?? '00:00').split(':').map(Number);
  const grossMinutes = (endH * 60 + endM) - (startH * 60 + startM);
  const breakMin = shift.break_minutes ?? 0;
  return Math.max(0, (grossMinutes - breakMin) / 60);
}

export function ShiftMonthView({
  shifts,
  absences,
  currentDate,
  employeeMap,
  onShiftClick,
  onCellClick,
  filterChildcareId: _filterChildcareId,
  filterGroupId,
}: ShiftMonthViewProps) {
  const { i18n } = useLingui();

  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const daysInMonth = useMemo(() => getDaysInMonth(currentDate), [currentDate]);

  // Build array of day dates for the month
  const days = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => addDays(monthStart, i));
  }, [monthStart, daysInMonth]);

  // Filter shifts by group if needed
  const filteredShifts = useMemo(() => {
    if (!filterGroupId) return shifts;
    return shifts.filter((s) =>
      s.groups?.some((g) => g.id === filterGroupId)
    );
  }, [shifts, filterGroupId]);

  // Build a set of unique employee IDs from the filtered shifts, sorted alphabetically
  const employeeIds = useMemo(() => {
    const ids = new Set<string>();
    let hasOpen = false;
    for (const shift of filteredShifts) {
      if (shift.employee_id) {
        ids.add(shift.employee_id);
      } else {
        hasOpen = true;
      }
    }
    // Also include employees with absences
    for (const absence of absences) {
      if (absence.employee_id) {
        ids.add(absence.employee_id);
      }
    }
    // Sort alphabetically by name
    const sorted = Array.from(ids).sort((a, b) => {
      const empA = employeeMap.get(a);
      const empB = employeeMap.get(b);
      const nameA = `${empA?.lastname ?? ''} ${empA?.firstname ?? ''}`.toLowerCase();
      const nameB = `${empB?.lastname ?? ''} ${empB?.firstname ?? ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    // Open shifts (#2040) get their own row at the end.
    return hasOpen ? [...sorted, OPEN_SHIFT_VALUE] : sorted;
  }, [filteredShifts, absences, employeeMap]);

  // Index shifts by employee_id + date for fast lookup
  const shiftsByEmployeeDate = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const shift of filteredShifts) {
      const key = `${shift.employee_id || OPEN_SHIFT_VALUE}_${shift.shift_date}`;
      const existing = map.get(key) ?? [];
      existing.push(shift);
      map.set(key, existing);
    }
    return map;
  }, [filteredShifts]);

  // Index absences by employee_id + date
  const absencesByEmployeeDate = useMemo(() => {
    const map = new Map<string, EmployeeAbsence[]>();
    for (const absence of absences) {
      if (!absence.from_date || !absence.to_date) continue;
      const start = parseISO(absence.from_date);
      const end = parseISO(absence.to_date);
      // Expand to each day in the range that falls in this month
      for (let d = start; d <= end; d = addDays(d, 1)) {
        const dateStr = format(d, 'yyyy-MM-dd');
        const key = `${absence.employee_id}_${dateStr}`;
        const existing = map.get(key) ?? [];
        existing.push(absence);
        map.set(key, existing);
      }
    }
    return map;
  }, [absences]);

  // Calculate total hours per day (for summary row)
  const totalHoursPerDay = useMemo(() => {
    const totals = new Map<string, number>();
    for (const day of days) {
      const dateStr = format(day, 'yyyy-MM-dd');
      let total = 0;
      for (const empId of employeeIds) {
        // An open shift is nobody's time yet.
        if (empId === OPEN_SHIFT_VALUE) continue;
        const key = `${empId}_${dateStr}`;
        const dayShifts = shiftsByEmployeeDate.get(key) ?? [];
        for (const s of dayShifts) {
          total += calcNetHours(s);
        }
      }
      totals.set(dateStr, total);
    }
    return totals;
  }, [days, employeeIds, shiftsByEmployeeDate]);

  // Calculate total hours per employee (for summary column)
  const totalHoursPerEmployee = useMemo(() => {
    const totals = new Map<string, number>();
    for (const empId of employeeIds) {
      let total = 0;
      for (const day of days) {
        const dateStr = format(day, 'yyyy-MM-dd');
        const key = `${empId}_${dateStr}`;
        const dayShifts = shiftsByEmployeeDate.get(key) ?? [];
        for (const s of dayShifts) {
          total += calcNetHours(s);
        }
      }
      totals.set(empId, total);
    }
    return totals;
  }, [employeeIds, days, shiftsByEmployeeDate]);

  const handleCellClick = useCallback(
    (day: Date, employeeId: string) => {
      onCellClick(day, employeeId);
    },
    [onCellClick]
  );

  if (employeeIds.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        {i18n._({ id: 'shifts.monthView.noShifts', message: 'No shifts' })}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full border-collapse text-xs">
          {/* Header row: day numbers */}
          <thead>
            <tr className="border-b">
              {/* Employee name column */}
              <th scope="col" className="sticky left-0 z-10 bg-background border-r p-2 text-left min-w-[140px] font-medium">
                {i18n._({ id: 'shifts.monthView.employee', message: 'Employee' })}
              </th>
              {days.map((day) => {
                const dayStr = format(day, 'yyyy-MM-dd');
                const weekend = isWeekend(day);
                const today = isToday(day);
                return (
                  <th
                    key={dayStr}
                    scope="col"
                    className={cn(
                      'p-1 text-center min-w-[36px] font-medium border-r',
                      weekend && 'bg-muted/50',
                      today && 'bg-primary/10'
                    )}
                  >
                    <div className="leading-tight">
                      <div>{formatWeekday(day, i18n.locale, 'short')}</div>
                      <div className={cn(today && 'font-bold text-primary')}>
                        {format(day, 'd')}
                      </div>
                    </div>
                  </th>
                );
              })}
              {/* Summary column */}
              <th scope="col" className="p-2 text-center min-w-[50px] font-medium bg-muted/30">
                {i18n._({ id: 'shifts.monthView.totalHours', message: 'Total Hours' })}
              </th>
            </tr>
          </thead>

          <tbody>
            {employeeIds.map((empId) => {
              const employee = employeeMap.get(empId);
              const empName =
                empId === OPEN_SHIFT_VALUE
                  ? i18n._({ id: 'shifts.open.row', message: 'Open shifts' })
                  : employee
                    ? `${employee.firstname ?? ''} ${employee.lastname ?? ''}`.trim()
                    : empId;
              const empTotalHours = totalHoursPerEmployee.get(empId) ?? 0;

              return (
                <tr key={empId} className="border-b hover:bg-muted/20">
                  {/* Employee name */}
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-background border-r p-2 text-left font-medium truncate max-w-[160px]"
                  >
                    {empName}
                  </th>

                  {/* Day cells */}
                  {days.map((day) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const key = `${empId}_${dateStr}`;
                    const cellShifts = shiftsByEmployeeDate.get(key) ?? [];
                    const cellAbsences = absencesByEmployeeDate.get(key) ?? [];
                    const weekend = isWeekend(day);
                    const today = isToday(day);

                    return (
                      <td
                        key={dateStr}
                        className={cn(
                          'border-r p-0.5 align-top cursor-pointer hover:bg-muted/40 transition-colors',
                          weekend && 'bg-muted/30',
                          today && 'bg-primary/5'
                        )}
                        onClick={() => {
                          if (cellShifts.length === 0) {
                            handleCellClick(day, empId);
                          }
                        }}
                      >
                        <div className="flex flex-col gap-0.5 min-h-[24px]">
                          {/* Absence bars */}
                          {cellAbsences.map((absence) => {
                            const label = getAbsenceTypeLabel(absence.absence_type ?? '', i18n);
                            return (
                              <div
                                key={absence.id}
                                className={cn(
                                  'rounded border px-0.5 text-xs leading-tight truncate',
                                  getAbsenceColor(absence.absence_type)
                                )}
                                title={label}
                              >
                                <span aria-hidden="true">{label.charAt(0)}</span>
                                <span className="sr-only">{label}</span>
                              </div>
                            );
                          })}

                          {/* Shift badges */}
                          {cellShifts.map((shift) => (
                            <Tooltip key={shift.id}>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className={cn(
                                    'rounded border px-0.5 text-xs leading-tight truncate w-full text-left',
                                    shiftBlockClasses(shift.status),
                                    !shift.employee_id && OPEN_SHIFT_BLOCK_CLASSES
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onShiftClick(shift);
                                  }}
                                >
                                  {shift.status === 'CONFIRMED' && (
                                    <Check className="inline-block h-2.5 w-2.5" aria-hidden="true" />
                                  )}
                                  {shift.start_time}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                <div className="space-y-1">
                                  <div className="font-medium">
                                    {shift.start_time} - {shift.end_time}
                                  </div>
                                  {(shift.break_minutes ?? 0) > 0 && (
                                    <div>
                                      {i18n._({
                                        id: 'shifts.monthView.breakLine',
                                        message: 'Break: {minutes} min',
                                        values: { minutes: shift.break_minutes },
                                      })}
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-1">
                                    {shift.shift_type && <ShiftTypeBadge type={shift.shift_type} />}
                                    {shift.status && <ShiftStatusBadge status={shift.status} />}
                                  </div>
                                  <div>
                                    {formatDecimal(calcNetHours(shift), i18n.locale, 1)} h
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </td>
                    );
                  })}

                  {/* Employee total hours */}
                  <td className="p-2 text-center font-medium bg-muted/20">
                    {formatDecimal(empTotalHours, i18n.locale, 1)}
                  </td>
                </tr>
              );
            })}

            {/* Summary row: total hours per day */}
            <tr className="border-t-2 bg-muted/30 font-medium">
              <th scope="row" className="sticky left-0 z-10 bg-muted/30 border-r p-2 text-left font-medium">
                {i18n._({ id: 'shifts.monthView.totalHours', message: 'Total Hours' })}
              </th>
              {days.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const total = totalHoursPerDay.get(dateStr) ?? 0;
                return (
                  <td key={dateStr} className="p-1 text-center border-r text-xs">
                    {total > 0 ? formatDecimal(total, i18n.locale, 1) : '-'}
                  </td>
                );
              })}
              {/* Grand total */}
              <td className="p-2 text-center font-bold">
                {formatDecimal(
                  Array.from(totalHoursPerEmployee.values()).reduce((sum, h) => sum + h, 0),
                  i18n.locale,
                  1
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}
